/**
 * Phase 14b — Analytics tracker (frontend).
 *
 * Provides:
 *   * usePostViewTracker(ref, post, { sourceFeed, sourceHashtag, communityId })
 *       — React hook. Uses IntersectionObserver to start a view session when
 *         the element is >= 50% in viewport; pings /heartbeat every 60s while
 *         it stays visible; calls /end on unmount or when it leaves view.
 *   * trackClick(postId, url, source) — fire-and-forget POST.
 *   * The view tracker silently no-ops on errors so analytics never break UX.
 *
 * Design notes
 * ------------
 * - Heartbeat interval is 60 000 ms per product spec.
 * - We use document.visibilityState — when the tab is hidden the heartbeat
 *   pauses so a user idling on a backgrounded tab doesn't pad numbers.
 * - Multiple FeedCards mount/unmount as the user scrolls; each one owns its
 *   own session. The 50% intersection threshold avoids registering "edge of
 *   viewport" flybys as full views.
 */
import { useEffect, useRef } from 'react';
import { api } from '@/lib/api';

const HEARTBEAT_MS = 60_000;
const VISIBILITY_THRESHOLD = 0.5;
const MIN_VIEW_MS_TO_LOG = 1_500; // ignore <1.5s glances

export async function trackClick(postId, url, opts = {}) {
  if (!postId || !url) return;
  try {
    await api.post('/analytics/click', {
      post_id: postId,
      url,
      source_feed: opts.sourceFeed || null,
      source_hashtag: opts.sourceHashtag || null,
      community_id: opts.communityId || null,
    });
  } catch (_) { /* swallow */ }
}

/**
 * @param {React.RefObject<HTMLElement>} ref
 * @param {object} post
 * @param {object} opts { sourceFeed, sourceHashtag, communityId, placement, enabled }
 */
export function usePostViewTracker(ref, post, opts = {}) {
  const sessionRef = useRef({
    viewId: null,        // backend-issued
    startedAt: null,     // wall-clock ms when intersection began
    accumulatedMs: 0,    // total visible time across re-intersections
    heartbeatTimer: null,
    posting: false,
    ended: false,
  });
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    if (!post || !post.id) return;
    if (opts.enabled === false) return;
    const node = ref && ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const sess = sessionRef.current;

    const flushDuration = () => {
      // If currently-visible, accumulate elapsed before flushing.
      if (sess.startedAt) {
        sess.accumulatedMs += Date.now() - sess.startedAt;
        sess.startedAt = Date.now(); // reset window so next flush is delta-only
      }
      return sess.accumulatedMs;
    };

    const startSession = async () => {
      if (sess.viewId || sess.posting) return;
      sess.posting = true;
      try {
        const { data } = await api.post('/analytics/view/start', {
          post_id: post.id,
          source_feed: optsRef.current.sourceFeed || null,
          source_hashtag: optsRef.current.sourceHashtag || null,
          community_id: optsRef.current.communityId || null,
          placement: optsRef.current.placement || 'feed',
        });
        sess.viewId = data && data.view_id;
      } catch (_) {
        // backend returned 4xx/5xx — abandon session, never retry to avoid spam
      } finally {
        sess.posting = false;
      }
    };

    const sendHeartbeat = async () => {
      if (!sess.viewId) return;
      const dur = flushDuration();
      try {
        await api.post('/analytics/view/heartbeat', {
          view_id: sess.viewId, duration_ms: dur,
        });
      } catch (_) { /* swallow */ }
    };

    const endSession = async () => {
      if (sess.ended) return;
      sess.ended = true;
      if (sess.heartbeatTimer) {
        clearInterval(sess.heartbeatTimer);
        sess.heartbeatTimer = null;
      }
      const dur = flushDuration();
      if (!sess.viewId || dur < MIN_VIEW_MS_TO_LOG) return;
      try {
        await api.post('/analytics/view/end', {
          view_id: sess.viewId, duration_ms: dur,
        });
      } catch (_) { /* swallow */ }
    };

    const onEnter = () => {
      if (!sess.viewId && !sess.posting) startSession();
      if (!sess.startedAt) sess.startedAt = Date.now();
      if (!sess.heartbeatTimer) {
        sess.heartbeatTimer = setInterval(() => {
          if (document.visibilityState === 'visible' && sess.startedAt) {
            sendHeartbeat();
          }
        }, HEARTBEAT_MS);
      }
    };

    const onLeave = () => {
      if (sess.startedAt) {
        sess.accumulatedMs += Date.now() - sess.startedAt;
        sess.startedAt = null;
      }
      if (sess.heartbeatTimer) {
        clearInterval(sess.heartbeatTimer);
        sess.heartbeatTimer = null;
      }
    };

    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
        onEnter();
      } else {
        onLeave();
      }
    }, { threshold: [0, VISIBILITY_THRESHOLD, 1] });
    io.observe(node);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') onLeave();
      // On return-to-visible we let IO re-fire if still intersecting.
    };
    document.addEventListener('visibilitychange', onVisibility);

    const onPageHide = () => { endSession(); };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);

    return () => {
      try { io.disconnect(); } catch (_) {}
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
      endSession();
    };
  }, [post && post.id, opts.enabled, ref]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ====== Aggregation API helpers consumed by analytics pages ======

export async function fetchMyOverview() {
  const { data } = await api.get('/analytics/me/overview');
  return data;
}

export async function fetchPostAnalytics(postId) {
  const { data } = await api.get(`/analytics/posts/${postId}`);
  return data;
}

export async function fetchAdminOverview() {
  const { data } = await api.get('/analytics/admin/overview');
  return data;
}

export async function fetchAdminReportsByCommunity(limit = 50) {
  const { data } = await api.get('/analytics/admin/reports/by-community', { params: { limit } });
  return data;
}

export async function fetchAdminReportsByUser(limit = 50) {
  const { data } = await api.get('/analytics/admin/reports/by-user', { params: { limit } });
  return data;
}
